import { useState } from 'react';
import { apiClient } from '../../lib/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

type Tab = 'amortization' | 'dscr' | 'ebitda';

export function CalculatorPage() {
  const [activeTab, setActiveTab] = useState<Tab>('amortization');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-navy mb-2">מחשבון מהיר</h1>
        <p className="text-slate-600">
          חישובים ללא שמירה בבסיס הנתונים. שימוש יומיומי בזמן שיחה מול לקוח.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <TabButton active={activeTab === 'amortization'} onClick={() => setActiveTab('amortization')}>
          לוח סילוקין
        </TabButton>
        <TabButton active={activeTab === 'dscr'} onClick={() => setActiveTab('dscr')}>
          DSCR דינמי
        </TabButton>
        <TabButton active={activeTab === 'ebitda'} onClick={() => setActiveTab('ebitda')}>
          EBITDA & FCF
        </TabButton>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'amortization' && <AmortizationCalc />}
        {activeTab === 'dscr' && <DSCRCalc />}
        {activeTab === 'ebitda' && <EBITDACalc />}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
        active
          ? 'border-business-primary text-business-primary'
          : 'border-transparent text-slate-600 hover:text-slate-900'
      }`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Amortization Calculator
// ---------------------------------------------------------------------------

function AmortizationCalc() {
  const [principal, setPrincipal] = useState('1000');
  const [rate, setRate] = useState('0.06');
  const [months, setMonths] = useState('60');
  const [loanType, setLoanType] = useState('SHPITZER');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handleCalc = async () => {
    setLoading(true);
    try {
      const r = await apiClient.post('/engines/amortization', {
        principal,
        annual_rate: rate,
        term_months: parseInt(months),
        loan_type: loanType,
        include_monthly: false,
      });
      setResult(r);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card space-y-4">
      <div className="grid md:grid-cols-4 gap-4">
        <LabeledInput
          label="קרן (אלפי ₪)"
          value={principal}
          onChange={setPrincipal}
        />
        <LabeledInput label="ריבית שנתית (0.07 = 7%)" value={rate} onChange={setRate} />
        <LabeledInput label="חודשים" value={months} onChange={setMonths} />
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">סוג הלוואה</label>
          <select
            value={loanType}
            onChange={(e) => setLoanType(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="SHPITZER">שפיצר</option>
            <option value="EQUAL_PRINCIPAL">קרן שווה</option>
            <option value="BULLET">בלון (בולט)</option>
            <option value="GRACE_FULL">גרייס מלא</option>
            <option value="GRACE_INTEREST">גרייס ריבית בלבד</option>
            <option value="ROLLING">מתגלגלת (חח"ד)</option>
          </select>
        </div>
      </div>

      <button onClick={handleCalc} disabled={loading} className="btn-primary">
        {loading ? 'מחשב...' : 'חשב'}
      </button>

      {result && (
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="סה״כ תשלומים" value={result.total_payments?.toFixed(2)} />
            <StatCard label="סה״כ ריבית" value={result.total_interest?.toFixed(2)} />
            <StatCard label="סה״כ קרן" value={result.total_principal?.toFixed(2)} />
          </div>

          {result.annual_schedule?.length > 0 && (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={result.annual_schedule}>
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="total_principal" fill="#2C5F8D" name="קרן" stackId="a" />
                  <Bar dataKey="total_interest" fill="#C89B3C" name="ריבית" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DSCR Calculator
// ---------------------------------------------------------------------------

function DSCRCalc() {
  const [cashFlow, setCashFlow] = useState('3967');
  const [principal, setPrincipal] = useState('46000');
  const [rate, setRate] = useState('0.07');
  const [years, setYears] = useState('25');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handleCalc = async () => {
    setLoading(true);
    try {
      const r = await apiClient.post('/engines/dscr/dynamic', {
        base_annual_cash_flow: cashFlow,
        loans: [
          {
            principal,
            annual_rate: rate,
            term_months: parseInt(years) * 12,
            loan_type: 'SHPITZER',
          },
        ],
        projection_years: parseInt(years),
        stress_scenarios: [
          { name: 'תזרים -10%', cashflow_shock: '-0.10' },
          { name: 'תזרים -20%', cashflow_shock: '-0.20' },
        ],
      });
      setResult(r);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card space-y-4">
      <div className="grid md:grid-cols-4 gap-4">
        <LabeledInput label="תזרים פנוי שנתי" value={cashFlow} onChange={setCashFlow} />
        <LabeledInput label="קרן ההלוואה" value={principal} onChange={setPrincipal} />
        <LabeledInput label="ריבית שנתית" value={rate} onChange={setRate} />
        <LabeledInput label="תקופה (שנים)" value={years} onChange={setYears} />
      </div>

      <button onClick={handleCalc} disabled={loading} className="btn-primary">
        {loading ? 'מחשב...' : 'חשב DSCR'}
      </button>

      {result && (
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <StatCard label="DSCR שנה 1" value={result.projections?.[0]?.dscr?.toFixed(3)} />
            <StatCard
              label="DSCR מינימלי"
              value={result.min_dscr?.toFixed(3)}
              subValue={`שנה ${result.min_dscr_year}`}
            />
            <StatCard label="שנים במצוקה" value={result.years_distressed} />
            <StatCard label="שנים דחוקות" value={result.years_tight} />
          </div>

          {result.stress_results?.length > 0 && (
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-semibold mb-2 text-navy">תרחישי רגישות</h4>
              <div className="space-y-2">
                {result.stress_results.map((s: any, i: number) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span>{s.scenario_name}</span>
                    <span className="font-mono">
                      DSCR מינ׳: {s.min_dscr?.toFixed(3)} | שנים במצוקה: {s.years_distressed}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EBITDA Calculator
// ---------------------------------------------------------------------------

function EBITDACalc() {
  const [opProfit, setOpProfit] = useState('1485.6');
  const [depreciation, setDepreciation] = useState('103');
  const [financing, setFinancing] = useState('492');
  const [tax, setTax] = useState('0');
  const [capex, setCapex] = useState('103');
  const [dividend, setDividend] = useState('0');
  const [result, setResult] = useState<any>(null);

  const handleCalc = async () => {
    const ebitdaRes = await apiClient.post('/engines/ebitda', {
      operating_profit: opProfit,
      depreciation_and_amortization: depreciation,
    });
    const fcfRes = await apiClient.post('/engines/free-cash-flow', {
      ebitda: ebitdaRes.ebitda.toString(),
      financing_expenses: financing,
      tax_expenses: tax,
      capex_current: capex,
      dividends: dividend,
    });
    setResult({ ebitda: ebitdaRes, fcf: fcfRes });
  };

  return (
    <div className="card space-y-4">
      <div className="grid md:grid-cols-3 gap-4">
        <LabeledInput label="רווח תפעולי" value={opProfit} onChange={setOpProfit} />
        <LabeledInput label="פחת והפחתות" value={depreciation} onChange={setDepreciation} />
        <LabeledInput label="הוצאות מימון" value={financing} onChange={setFinancing} />
        <LabeledInput label="מס" value={tax} onChange={setTax} />
        <LabeledInput label="CapEx" value={capex} onChange={setCapex} />
        <LabeledInput label="דיבידנד" value={dividend} onChange={setDividend} />
      </div>

      <button onClick={handleCalc} className="btn-primary">
        חשב EBITDA & FCF
      </button>

      {result && (
        <div className="mt-6 grid grid-cols-2 gap-4">
          <StatCard label="EBITDA" value={result.ebitda.ebitda?.toFixed(2)} />
          <StatCard
            label="תזרים פנוי"
            value={result.fcf.free_cash_flow?.toFixed(2)}
            danger={result.fcf.is_negative}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared components
// ---------------------------------------------------------------------------

function LabeledInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm num"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  subValue,
  danger = false,
}: {
  label: string;
  value: string | number | undefined;
  subValue?: string;
  danger?: boolean;
}) {
  return (
    <div className="bg-slate-50 rounded-lg p-4">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold num ${danger ? 'text-red-600' : 'text-navy'}`}>
        {value ?? '-'}
      </div>
      {subValue && <div className="text-xs text-slate-500 mt-1">{subValue}</div>}
    </div>
  );
}
